import React, { useRef, forwardRef, useImperativeHandle } from 'react';
import { useReactToPrint } from 'react-to-print';
import dayjs from 'dayjs';

interface PrintRef {
  print: () => void;
}

interface PrescriptionPrintComponentProps {
  record: any;
  patient?: any;
}

export const PrescriptionPrintComponent = forwardRef<
  PrintRef,
  PrescriptionPrintComponentProps
>(({ record, patient }, ref) => {
  const componentRef = useRef<HTMLDivElement>(null);
  const data = record;

  const handlePrint = useReactToPrint({
    contentRef: componentRef,
    documentTitle: `Prescription-${data.id || 'New'}`,
    onBeforePrint: async () => {
      // Add print-specific styles
      const style = document.createElement('style');
      style.innerHTML = `
          @media print {
            body * {
              visibility: hidden;
            }
            #prescription-print, #prescription-print * {
              visibility: visible;
            }
            #prescription-print {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
            }
          }
        `;
      document.head.appendChild(style);
    },
    onAfterPrint: () => {
      // Clean up print-specific styles
      const style = document.querySelector('style[data-print-style]');
      if (style) {
        style.remove();
      }
    },
  });

  useImperativeHandle(ref, () => ({
    print: handlePrint,
  }));

  // Helper function to safely format date
  const formatDate = (date: string | undefined | null) => {
    if (!date) return '';
    return dayjs(date).format('DD/MM/YYYY');
  };

  // Helper function to calculate age
  const calculateAge = (birthDate: string | null) => {
    if (!birthDate) return 'N/A';
    return dayjs().diff(dayjs(birthDate), 'year');
  };

  // Helper function to format text with bullets for commas and new lines
  const formatText = (text: string | undefined | null) => {
    if (!text) return null;

    // Replace literal \n with actual line breaks and split
    const processedText = text.replace(/\\n/g, '\n');

    // Split by actual line breaks and handle each line
    return processedText.split('\n').map((line, lineIndex) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return null;

      // If line contains commas, create bullet points
      if (trimmedLine.includes(',')) {
        return (
          <div key={lineIndex} className="flex flex-col gap-1">
            {trimmedLine.split(',').map((item, itemIndex) => {
              const trimmedItem = item.trim();
              if (!trimmedItem) return null;
              return (
                <div
                  key={`${lineIndex}-${itemIndex}`}
                  className="flex items-start gap-2 text-xs leading-4"
                >
                  <span>•</span>
                  <span>{trimmedItem}</span>
                </div>
              );
            })}
          </div>
        );
      }

      // If no commas, just show the line with proper line break
      return (
        <div key={lineIndex} className="text-xs leading-4 whitespace-pre-line">
          {trimmedLine}
        </div>
      );
    });
  };

  return (
    <div className="hidden print:block">
      <div
        ref={componentRef}
        id="prescription-print"
        className="bg-white min-h-screen"
        style={{
          paddingLeft: '2rem',
          paddingRight: '2rem',
          fontFamily: 'Arial, sans-serif',
        }}
      >
        <div className="max-w-4xl mx-auto pt-[8rem] pl-10 pr-4 pb-4">
          {/* Patient Information Section */}
          <div className="grid grid-cols-2 gap-4 mb-4 pt-8">
            {/* Patient Information */}
            <div
              className="p-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 mb-2 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Patient Information
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Name:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {patient || data.patient
                      ? `${(patient || data.patient).first_name || ''} ${(patient || data.patient).last_name || ''}`
                      : 'N/A'}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Patient ID:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {patient?.id || data.patient_id}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Age:
                  </span>
                  <span className="text-gray-900 text-xs">
                    {patient?.birth_date || data.patient?.birth_date
                      ? `${calculateAge(patient?.birth_date || data.patient?.birth_date)} years`
                      : 'N/A'}
                  </span>
                </div>
              </div>
            </div>

            {/* Prescription Details */}
            <div
              className="p-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 mb-2 pb-1"
                style={{ borderBottom: '1px solid #d1d5db' }}
              >
                Prescription Details
              </h4>
              <div className="space-y-1">
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Date:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    {formatDate(data.prescribed_at)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="font-medium text-gray-700 text-xs">
                    Prescription #:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs">
                    PRES-{data.id || 'NEW'}
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Medicines Table */}
          <div className="mb-4">
            <h4 className="text-xs font-semibold text-gray-900">
              Medicines & Dosage
            </h4>
            <div
              className="rounded-lg overflow-hidden"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <table className="w-full">
                <thead>
                  <tr style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <th className="px-2 py-2 text-left font-semibold text-xs text-gray-900">
                      Medicine
                    </th>
                    <th className="px-2 py-2 text-left font-semibold text-xs text-gray-900">
                      Dosage
                    </th>
                    {data.prescription_medicines?.some(
                      (m: any) => m.when_take
                    ) && (
                      <th className="px-2 py-2 text-left font-semibold text-xs text-gray-900">
                        When
                      </th>
                    )}
                    {data.prescription_medicines?.some(
                      (m: any) => m.where_take
                    ) && (
                      <th className="px-2 py-2 text-left font-semibold text-xs text-gray-900">
                        Where
                      </th>
                    )}
                    <th className="px-2 py-2 text-center font-semibold text-xs text-gray-900">
                      Qty.
                    </th>
                    {data.prescription_medicines?.some(
                      (m: any) => m.instruction
                    ) && (
                      <th className="px-2 py-2 text-left font-semibold text-xs text-gray-900">
                        Remark
                      </th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {data.prescription_medicines?.map(
                    (medicine: any, index: number) => (
                      <tr
                        key={`medicine-${index}`}
                        style={{ borderTop: '1px solid #e5e7eb' }}
                      >
                        <td className="px-2 py-1.5 text-gray-900 font-medium text-xs">
                          {medicine.name || ''}
                        </td>
                        <td className="px-2 py-1.5 text-gray-700 text-xs">
                          {medicine.dosage || ''}
                        </td>
                        {data.prescription_medicines?.some(
                          (m: any) => m.when_take
                        ) && (
                          <td className="px-2 py-1.5 text-gray-700 text-xs">
                            {medicine.when_take || ''}
                          </td>
                        )}
                        {data.prescription_medicines?.some(
                          (m: any) => m.where_take
                        ) && (
                          <td className="px-2 py-1.5 text-gray-700 text-xs">
                            {medicine.where_take || ''}
                          </td>
                        )}
                        <td className="px-2 py-1.5 text-gray-700 text-xs text-center">
                          {medicine.qty || ''}
                        </td>
                        {data.prescription_medicines?.some(
                          (m: any) => m.instruction
                        ) && (
                          <td className="px-2 py-1.5 text-gray-700 text-xs">
                            {medicine.instruction || ''}
                          </td>
                        )}
                      </tr>
                    )
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Additional Information */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            {/* Lab Reports */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                Lab Reports
              </h4>
              <div className="min-h-[50px] p-1 rounded text-gray-700 text-xs">
                {data.lab_report ? (
                  <div className="space-y-1">
                    {(() => {
                      // Handle multiple levels of escaping by recursively replacing \\n with \n
                      let processedText = data.lab_report;
                      while (processedText.includes('\\n')) {
                        processedText = processedText.replace(/\\n/g, '\n');
                      }
                      return processedText
                        .split('\n')
                        .map((line: string, index: number) => {
                          const trimmedLine = line.trim();
                          if (!trimmedLine) return null;
                          return (
                            <div key={index} className="flex items-start gap-2">
                              <span>•</span>
                              <span>{trimmedLine}</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                ) : (
                  <span className="text-gray-500">No lab reports</span>
                )}
              </div>
            </div>

            {/* Today's Procedures */}
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                Today's Procedures
              </h4>
              <div className="min-h-[50px] p-1 rounded text-gray-700 text-xs">
                {data.today_procedure ? (
                  <div className="space-y-1">
                    {(() => {
                      // Handle multiple levels of escaping by recursively replacing \\n with \n
                      let processedText = data.today_procedure;
                      while (processedText.includes('\\n')) {
                        processedText = processedText.replace(/\\n/g, '\n');
                      }
                      return processedText
                        .split('\n')
                        .map((line: string, index: number) => {
                          const trimmedLine = line.trim();
                          if (!trimmedLine) return null;
                          return (
                            <div key={index} className="flex items-start gap-2">
                              <span>•</span>
                              <span>{trimmedLine}</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                ) : (
                  <span className="text-gray-500">No procedures</span>
                )}
              </div>
            </div>
          </div>

          {/* Advice Section */}
          <div className="mb-4">
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                Advice
              </h4>
              <div className="min-h-[50px] p-1 rounded text-gray-700 text-xs">
                {data.notes ? (
                  <div className="space-y-1">
                    {(() => {
                      // Handle multiple levels of escaping by recursively replacing \\n with \n
                      let processedText = data.notes;
                      while (processedText.includes('\\n')) {
                        processedText = processedText.replace(/\\n/g, '\n');
                      }
                      return processedText
                        .split('\n')
                        .map((line: string, index: number) => {
                          const trimmedLine = line.trim();
                          if (!trimmedLine) return null;
                          return (
                            <div key={index} className="flex items-start gap-2">
                              <span>•</span>
                              <span>{trimmedLine}</span>
                            </div>
                          );
                        });
                    })()}
                  </div>
                ) : (
                  <span className="text-gray-500">No additional advice</span>
                )}
              </div>
            </div>
          </div>

          {/* Follow-up and Signature */}
          <div className="grid grid-cols-2 gap-4">
            <div
              className="py-2 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                Follow-up Details
              </h4>
              <div className="space-y-1">
                <div className="flex justify-start items-center">
                  <span className="font-medium text-gray-700 text-xs">
                    Follow-up Date:
                  </span>
                  <span className="text-gray-900 font-semibold text-xs ml-2">
                    {data.patient?.follow_up_date
                      ? formatDate(data.patient.follow_up_date)
                      : 'Not scheduled'}
                  </span>
                </div>
              </div>
            </div>

            <div
              className="py-1 px-2 rounded-lg"
              style={{ border: '1px solid #e5e7eb' }}
            >
              <h4
                className="text-xs font-semibold text-gray-900 pb-1"
                style={{ borderBottom: '1px solid #e5e7eb' }}
              >
                Doctor's Signature
              </h4>
              <div className="min-h-[40px] flex items-end">
                <div className="w-full border-b-2 border-gray-400 pb-1">
                  <span className="text-gray-500 text-xs">
                    Doctor's signature here
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div
            className="mt-4 pt-1 text-center"
            style={{ borderTop: '1px solid #e5e7eb' }}
          >
            <p className="text-gray-600 text-xs mb-1">
              Thank you for choosing Cresence Skin for your skin care needs
            </p>
            <p className="text-gray-500 text-xs">
              For any queries, please contact us at +91 74900 20099
            </p>
          </div>
        </div>
      </div>
    </div>
  );
});

PrescriptionPrintComponent.displayName = 'PrescriptionPrintComponent';
